import React, { useEffect, useRef, useState } from "react";
import { apiRequest } from "../api/client.js";
import { catchUp, cueFor, findWorkout, introFor, timedTotalSeconds } from "../shared/workouts.js";
import { beep, say, setMuted, stop, unlock } from "../shared/speak.js";
import { HeatPanel } from "./MockTestTools.jsx";
import { ArrowLeft, ChevronLeft, ChevronRight, Check } from "../shared/icons.jsx";
import { Button, cx } from "../shared/ui.jsx";
import { mmss } from "../shared/dates.js";

const DIGIT_WORDS = { 5: "five", 4: "four", 3: "three", 2: "two", 1: "one" };
const RING = 2 * Math.PI * 52; // countdown ring circumference

const segLabel = (s) => (s.kind === "laps" ? `${s.laps} laps` : mmss(s.seconds));

// Full-screen guided workout. One leader device leads the group: it speaks each
// exercise, runs the per-part timer, counts down the last 5s, and hands lap
// segments to the heat stopwatch. Timing is wall-clock (absolute timestamps) so a
// locked/backgrounded phone resumes in the right place. See src/shared/speak.js
// for audio + src/shared/workouts.js for the segment model.
export default function WorkoutPlayer({ event, close, changed }) {
  const workout = findWorkout(event.workoutId);
  const segments = workout?.segments || [];

  const [phase, setPhase] = useState("overview"); // overview | run | done
  const [index, setIndex] = useState(0);
  const [segStartMs, setSegStartMs] = useState(0);
  const [now, setNow] = useState(0);
  const [paused, setPaused] = useState(false);
  const [pausedAtMs, setPausedAtMs] = useState(0);
  const [muted, setMutedState] = useState(false);

  const lastDigit = useRef(null);
  const logged = useRef(false);

  const seg = segments[index] || null;
  const nextSeg = segments[index + 1] || null;
  const refNow = paused ? pausedAtMs : now;

  // Announce a segment: a transition tone (the audible "go") + its spoken cue.
  // On a rest/transition, follow with "Next up, <exercise>" so the group knows
  // what's coming. ponytail: the beep is the go-signal, so non-rest cues don't
  // each tack on "Go." (lap cues already end in "Go.").
  const announce = (s, next) => {
    if (!s) return;
    beep(990, 150);
    if (s.rest && next) {
      say(cueFor(s)).then(() => say("Next up.")).then(() => say(`${next.name}.`));
    } else {
      say(cueFor(s));
    }
  };

  const finish = async () => {
    stop();
    setPhase("done");
    beep(880, 220);
    say("Workout complete.");
    if (!logged.current) {
      logged.current = true;
      try {
        await apiRequest("log_workout", { method: "POST", body: { eventId: event.id, workoutId: workout.id } });
        await changed?.();
      } catch { /* logging is best-effort; don't block the leader */ }
    }
  };

  // Move to a segment (or finish past the end), resetting the countdown + clock.
  const goTo = (target, startedMs) => {
    lastDigit.current = null;
    if (target >= segments.length) { finish(); return; }
    setIndex(target);
    setSegStartMs(startedMs ?? Date.now());
    announce(segments[target], segments[target + 1]);
  };

  const start = async () => {
    await unlock();
    setMuted(muted);
    logged.current = false;
    lastDigit.current = null;
    const t = Date.now();
    setIndex(0);
    setSegStartMs(t);
    setNow(t);
    setPaused(false);
    setPhase("run");
    beep(990, 150);
    say(introFor(workout)).then(() => announce(segments[0], segments[1]));
  };

  const togglePause = () => {
    if (paused) {
      setSegStartMs((s) => s + (Date.now() - pausedAtMs));
      setPaused(false);
    } else {
      stop();
      setPausedAtMs(Date.now());
      setPaused(true);
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMutedState(next);
    setMuted(next);
  };

  const testSound = async () => {
    await unlock();
    setMuted(muted);
    beep(990, 150);
    say("Get ready.");
  };

  // Tick while running.
  useEffect(() => {
    if (phase !== "run" || paused) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [phase, paused]);

  // Timed-segment engine: catch up across elapsed parts (resume-in-place) and
  // speak the 5-4-3-2-1 countdown. Lap segments wait for the leader's Next tap.
  useEffect(() => {
    if (phase !== "run" || paused || !seg || seg.kind !== "timed") return;
    const caught = catchUp(segments, index, segStartMs, refNow);
    if (caught.index !== index) { goTo(caught.index, caught.startedMs); return; }
    const remainingMs = segStartMs + seg.seconds * 1000 - refNow;
    const remSec = Math.ceil(remainingMs / 1000);
    if (remSec >= 1 && remSec <= 5 && lastDigit.current !== remSec) {
      lastDigit.current = remSec;
      beep(660, 110);
      say(DIGIT_WORDS[remSec]);
    }
  }, [now, index, phase, paused]);

  // Keep the screen on while running; re-acquire after a tab switch.
  useEffect(() => {
    let lock = null;
    if (phase !== "run" || !navigator.wakeLock?.request) return undefined;
    const acquire = async () => { try { lock = await navigator.wakeLock.request("screen"); } catch { /* best effort */ } };
    acquire();
    const visible = () => { if (document.visibilityState === "visible" && (!lock || lock.released)) acquire(); };
    document.addEventListener("visibilitychange", visible);
    return () => { document.removeEventListener("visibilitychange", visible); lock?.release?.(); };
  }, [phase]);

  // Stop any audio when the player unmounts.
  useEffect(() => () => stop(), []);

  const shell = (children) => (
    <div className="fixed inset-0 z-50 bg-ink overflow-y-auto" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div className="w-full max-w-screen-sm mx-auto min-h-full flex flex-col">{children}</div>
    </div>
  );

  if (!workout || segments.length === 0) {
    return shell(
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-white">This event has no runnable workout.</p>
        <Button variant="dark" onClick={close}>Back</Button>
      </div>
    );
  }

  const muteBtn = (
    <button
      onClick={toggleMute}
      className={cx(
        "mono text-[10px] font-semibold uppercase tracking-[0.14em] border px-2.5 py-1.5 bg-transparent cursor-pointer",
        muted ? "border-fail text-[#e07a7a]" : "border-ink-line text-muted"
      )}
    >
      {muted ? "Muted" : "Sound on"}
    </button>
  );

  // ── Overview / Start ───────────────────────────────────────────────────────
  if (phase === "overview") {
    const lapBlocks = segments.filter((s) => s.kind === "laps").length;
    return shell(
      <>
        <div className="sticky top-0 z-10 bg-ink flex items-center justify-between px-4 py-3">
          <button onClick={close} className="flex items-center gap-2 bg-transparent border-0 text-white mono text-[11px] uppercase tracking-[0.12em] cursor-pointer p-1">
            <ArrowLeft size={18} />Event
          </button>
          {muteBtn}
        </div>
        <div className="px-5 pb-4">
          <div className="mono text-[10px] uppercase tracking-[0.16em] text-signal-bright mb-1">Guided workout</div>
          <h2 className="display text-4xl leading-[0.9] text-white m-0">{workout.title}</h2>
          <div className="mono text-[11px] uppercase tracking-[0.14em] text-muted mt-2">
            ~{Math.round(timedTotalSeconds(workout) / 60)} min timed{lapBlocks ? ` · ${lapBlocks} lap block${lapBlocks > 1 ? "s" : ""}` : ""} · {event.title}
          </div>
        </div>
        <div className="flex-1 bg-surface px-5 py-4">
          <p className="text-sm leading-relaxed text-stone-700 m-0 whitespace-pre-wrap">{workout.notes}</p>
        </div>
        <div className="sticky bottom-0 bg-ink border-t border-ink-line px-4 pt-3.5 pb-[calc(1.1rem+env(safe-area-inset-bottom))] space-y-2.5">
          <button
            onClick={testSound}
            className="w-full h-11 bg-transparent border border-ink-line text-white mono text-[11px] font-semibold uppercase tracking-[0.16em] cursor-pointer"
          >
            Test sound
          </button>
          <Button size="lg" className="w-full" onClick={start}>Start workout</Button>
        </div>
      </>
    );
  }

  // ── Done ───────────────────────────────────────────────────────────────────
  if (phase === "done") {
    return shell(
      <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8 text-center">
        <Check size={56} className="text-signal-bright" />
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted mb-2">Workout complete</div>
          <h2 className="display text-4xl leading-[0.9] text-white m-0">{workout.title}</h2>
        </div>
        <Button size="lg" onClick={close}>Done</Button>
      </div>
    );
  }

  // ── Running ────────────────────────────────────────────────────────────────
  const controlBar = (
    <div className="sticky top-0 z-10 bg-ink flex items-center justify-between px-4 py-3 border-b border-ink-line">
      <button onClick={close} className="flex items-center gap-2 bg-transparent border-0 text-white mono text-[11px] uppercase tracking-[0.12em] cursor-pointer p-1">
        <ArrowLeft size={18} />End
      </button>
      <div className="flex items-center gap-2">
        {muteBtn}
        <button onClick={() => goTo(Math.max(0, index - 1), Date.now())} aria-label="Previous" className="w-9 h-9 border border-ink-line text-white bg-transparent cursor-pointer flex items-center justify-center">
          <ChevronLeft size={18} />
        </button>
        <button onClick={togglePause} className="mono text-[10px] font-semibold uppercase tracking-[0.14em] border border-ink-line text-white px-3 h-9 bg-transparent cursor-pointer">
          {paused ? "Resume" : "Pause"}
        </button>
        <button onClick={() => goTo(index + 1, Date.now())} aria-label="Skip" className="w-9 h-9 border border-ink-line text-white bg-transparent cursor-pointer flex items-center justify-center">
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );

  const progress = seg.round ? `Round ${seg.round} of ${seg.rounds}` : `Part ${index + 1} of ${segments.length}`;

  if (seg.kind === "laps") {
    return shell(
      <>
        {controlBar}
        <div className="bg-ink px-5 pt-3 pb-4 text-center">
          <div className="mono text-[10px] uppercase tracking-[0.16em] text-signal-bright mb-1">{progress}</div>
          <h2 className="display text-4xl leading-[0.92] text-white m-0">{seg.name}</h2>
          <div className="mono text-[10px] uppercase tracking-[0.14em] text-muted mt-2">
            Run the laps on the stopwatch — tap Next when the group's done
          </div>
          <Button size="lg" className="w-full mt-3" onClick={() => goTo(index + 1, Date.now())}>
            {nextSeg ? `Next · ${nextSeg.name}` : "Finish workout"}
          </Button>
        </div>
        <div className="flex-1 bg-surface">
          <HeatPanel event={event} changed={() => {}} defaultLaps={seg.laps} title="LAP STOPWATCH" />
        </div>
      </>
    );
  }

  // Timed segment.
  const remainingMs = segStartMs + seg.seconds * 1000 - refNow;
  const remSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const fraction = Math.max(0, Math.min(1, remainingMs / (seg.seconds * 1000)));
  return shell(
    <>
      {controlBar}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-6 text-center">
        <div className="mono text-[11px] uppercase tracking-[0.18em] text-signal-bright mb-3">{progress}</div>
        <h2 className="display text-[clamp(2.5rem,11vw,4.5rem)] leading-[0.9] text-white m-0 mb-6 break-words">{seg.name}</h2>
        <div className="relative w-[160px] h-[160px]">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#2b2f36" strokeWidth="8" />
            <circle
              cx="60" cy="60" r="52" fill="none"
              stroke={remSec <= 5 ? "#e07a7a" : "#f0b323"}
              strokeWidth="8" strokeLinecap="round"
              strokeDasharray={RING}
              strokeDashoffset={RING * (1 - fraction)}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="display text-[56px] leading-none text-white tabular-nums">{remSec <= 60 ? remSec : mmss(remSec)}</span>
          </div>
        </div>
        {paused && <div className="mono text-[11px] uppercase tracking-[0.16em] text-warn mt-5">Paused</div>}
      </div>
      <div className="bg-ink-soft border-t border-ink-line px-5 py-4 text-center">
        <div className="mono text-[9px] uppercase tracking-[0.16em] text-muted mb-1">Next up</div>
        <div className="text-lg font-semibold text-white">{nextSeg ? `${nextSeg.name} · ${segLabel(nextSeg)}` : "Finish"}</div>
      </div>
    </>
  );
}
